import React, { useEffect } from "react";
import { currencyFormatter } from "../../utils/coins";
import {
  toNumber,
  formatCoin,
  formatStakeDate,
  formatApy,
} from "../../utils/stakes";

const statusStyles = {
  active: "bg-blue-600 text-white",
  matured: "bg-amber-500 text-black",
  settled: "bg-gray-600 text-white",
};

export default function StakeCard({ stake }) {
  const s = stake || {};
  const status = s.status || "active";
  const apyLabel = formatApy(s.apy);
  const matures = formatStakeDate(s.matures_at);
  const coinAmount = toNumber(s.coin_amount);
  const interestAmount = toNumber(s.interest_amount);
  const payoutAmount = toNumber(s.payout_amount);
  const payoutUsd = toNumber(s.payout_usd);
  const usdPrincipal = toNumber(s.amount);
  const interestLabel =
    status === "settled" ? "Interest earned" : "Projected interest";
  const payoutLabel = status === "settled" ? "Payout (credited)" : "Projected payout";
  const settledDate = formatStakeDate(s.settled_at);

  useEffect(() => {
    if (s.settlement_error) {
      console.error("Stake settlement failed:", {
        staking_id: s.staking_id,
        error: s.settlement_error,
      });
    }
  }, [s.staking_id, s.settlement_error]);

  return (
    <div className="border-1 w-72 bg-black border border-gray-500 rounded p-4 py-3 text-white">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-blue-400">
        <p className="text-xs text-slate-100">ID: {s.staking_id}</p>
        <span
          className={`text-[10px] uppercase px-2 py-0.5 rounded ${
            statusStyles[status] || statusStyles.active
          }`}
        >
          {status}
        </span>
      </div>

      <div className="flex justify-between items-end mb-3">
        <div className="flex flex-col items-start leading-7 text-green-100">
          <p className="mr-2 text-3xl">
            {usdPrincipal === null ? "—" : currencyFormatter.format(usdPrincipal)}
          </p>
          <p className="text-sm">
            {coinAmount === null
              ? "Coin amount unavailable"
              : `${formatCoin(coinAmount)} ${s.coin ?? ""}`}
          </p>
        </div>
      </div>

      <div className="text-xs space-y-1 mt-3 pt-2 text-slate-100 border-t border-blue-400">
        <div className="flex justify-between">
          <span>Matures</span>
          <span>{matures || "Open-ended"}</span>
        </div>
        {interestAmount !== null && (
          <div className="flex justify-between">
            <span>{interestLabel}</span>
            <span>{`${formatCoin(interestAmount)} ${s.coin ?? ""}`}</span>
          </div>
        )}
        {payoutAmount !== null && (
          <div className="flex justify-between">
            <span>{payoutLabel}</span>
            <span>{`${formatCoin(payoutAmount)} ${s.coin ?? ""}`}</span>
          </div>
        )}
        {payoutUsd !== null && (
          <div className="flex justify-between text-gray-400">
            <span>≈ USD</span>
            <span>{currencyFormatter.format(payoutUsd)}</span>
          </div>
        )}
        {settledDate && (
          <div className="flex justify-between">
            <span>Settled</span>
            <span>{settledDate}</span>
          </div>
        )}
        {apyLabel && (
          <div className="flex justify-between text-gray-500">
            <span>APY</span>
            <span>{apyLabel}</span>
          </div>
        )}
      </div>

      {status === "matured" && (
        <p className="text-xs mt-3 pt-2 text-amber-400 border-t border-blue-400">
          Payout is being processed. If this takes longer than expected, contact support.
        </p>
      )}
    </div>
  );
}